import { Col, Row, Switch } from "antd";
import dayjs from "dayjs";
import { get, isEmpty, set } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOutlineFrontHand } from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  TERMINATION_MOTIFS,
  TERMINATION_RECIPIENTS,
} from "utils/api/graphql/queries/termination";
import { DATE_FORMAT, SUBSCRIPTION_FORM_KEYS } from "utils/constants";
import { filterContractNumber } from "utils/helpers/validations";

const TerminationRequest = ({
  formProps,
  values,
  initialValues,
  setPercent,
  errors,
  isFieldRequired,
  setDisabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  return (
    get(initialValues, "project.ria", false) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST}-section`}
      >
        <Form
          type="vertical"
          title={
            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.TITLE
          }
          subtitle={
            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST
              .SUBTITLE
          }
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert
                showIcon
                type="info"
                message={SUBSCRIPTION.FORM.TERMINATION_ALERT}
              />
            </Col>
            <Col lg={24} xs={24}>
              <div className="d-flex justify--between">
                <span className="contact-stepper__content--radio--title">
                  L'adhérent souhaite que La Mutuelle Familiale s'occupe de la
                  résiliation.
                </span>
                <Switch
                  className="contact-stepper__content--radio--switch button__switch"
                  checkedChildren={"Oui"}
                  unCheckedChildren={"Non"}
                  defaultChecked={
                    !get(initialValues, "project.fields.selfOccupation")
                  }
                  onChange={(e) => {
                    set(initialValues, "project.fields.selfOccupation", !e);
                    setPercent(initialValues);
                    setDisabled((prev) => ({
                      ...prev,
                      project: {
                        ...prev?.project,
                        terminationRequest: {
                          ...prev?.project?.terminationRequest,
                          contractNumber: !e,
                          dueDate: !e,
                          recipient: !e,
                          reason: !e,
                        },
                      },
                    }));
                    setPercent(initialValues);
                  }}
                  onBlur={() => setPercent(initialValues)}
                />
              </div>
            </Col>
            {get(initialValues, "project.ria") &&
              !get(initialValues, "project.fields.selfOccupation") && (
                <>
                  <Col lg={24} xs={24}>
                    <Input
                      placeholder={
                        SUBSCRIPTION.FORM.CONTRACT_NUMBER.PLACEHOLDER
                      }
                      label={SUBSCRIPTION.FORM.CONTRACT_NUMBER.LABEL}
                      icon={<MdOutlineFrontHand size={16} />}
                      defaultValue={get(
                        initialValues,
                        "project.terminationRequest.contractNumber",
                      )}
                      errors={
                        errors["project.terminationRequest.contractNumber"]
                      }
                      touched={
                        !isEmpty(
                          errors["project.terminationRequest.contractNumber"],
                        )
                      }
                      onChange={({ target: { value } }) => {
                        const filteredValue = filterContractNumber(value);
                        set(
                          initialValues,
                          "project.terminationRequest.contractNumber",
                          filteredValue,
                        );
                      }}
                      onBlur={() => setPercent(initialValues)}
                      required={isFieldRequired(
                        "project.terminationRequest.contractNumber",
                      )}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Input
                      label={SUBSCRIPTION.FORM.CURRENT_EFFECTIVE_DATE.LABEL}
                      placeholder={
                        SUBSCRIPTION.FORM.CURRENT_EFFECTIVE_DATE.PLACEHOLDER
                      }
                      type="date"
                      variant="simple"
                      format={DATE_FORMAT}
                      disabledDate={(current) => {
                        return current && current > dayjs();
                      }}
                      defaultValue={
                        get(initialValues, "project.terminationRequest.dueDate")
                          ? dayjs(
                              get(
                                initialValues,
                                "project.terminationRequest.dueDate",
                              ),
                            )
                          : undefined
                      }
                      errors={errors["project.terminationRequest.dueDate"]}
                      touched={
                        !isEmpty(errors["project.terminationRequest.dueDate"])
                      }
                      onChange={(date) => {
                        const isValid = date && date < dayjs();
                        if (!isValid)
                          return set(
                            initialValues,
                            "project.terminationRequest.dueDate",
                            null,
                          );
                        set(
                          initialValues,
                          "project.terminationRequest.dueDate",
                          date,
                        );
                      }}
                      onBlur={({ target: { value } }) => {
                        const date = dayjs(value, DATE_FORMAT);
                        const isValid = date.isValid() && date < dayjs();
                        if (!isValid)
                          return set(
                            initialValues,
                            "project.terminationRequest.dueDate",
                            null,
                          );
                        set(
                          initialValues,
                          "project.terminationRequest.dueDate",
                          date,
                        );
                        setPercent(initialValues);
                      }}
                      required={isFieldRequired(
                        "project.terminationRequest.dueDate",
                      )}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Select
                      type="async"
                      node={{
                        query: TERMINATION_RECIPIENTS,
                        variables: {
                          where: { id: "54" },
                        },
                        accessors: {
                          root: "terminationRecipients.data",
                          label: "marque",
                          value: "id",
                        },
                      }}
                      label={SUBSCRIPTION.FORM.CURRENT_COMPANY.LABEL}
                      placeholder={
                        SUBSCRIPTION.FORM.CURRENT_COMPANY.PLACEHOLDER
                      }
                      defaultValue={get(
                        initialValues,
                        "project.terminationRequest.recipient",
                      )}
                      errors={errors["project.terminationRequest.recipient"]}
                      touched={
                        !isEmpty(errors["project.terminationRequest.recipient"])
                      }
                      onChange={(value) => {
                        set(
                          initialValues,
                          "project.terminationRequest.recipient",
                          value,
                        );
                      }}
                      onBlur={() => setPercent(initialValues)}
                      required={isFieldRequired(
                        "project.terminationRequest.recipient",
                      )}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Select
                      type="async"
                      node={{
                        query: TERMINATION_MOTIFS,
                        variables: {},
                        accessors: {
                          root: "terminationMotifs.data",
                          label: "nom",
                          value: "id",
                        },
                      }}
                      value={get(values, "project.terminationRequest.reason")}
                      label={SUBSCRIPTION.FORM.TERMINATION_MOTIFS.LABEL}
                      placeholder={
                        SUBSCRIPTION.FORM.TERMINATION_MOTIFS.PLACEHOLDER
                      }
                      defaultValue={get(
                        initialValues,
                        "project.terminationRequest.reason",
                      )}
                      errors={errors["project.terminationRequest.reason"]}
                      touched={
                        !isEmpty(errors["project.terminationRequest.reason"])
                      }
                      onChange={(value) => {
                        set(
                          initialValues,
                          "project.terminationRequest.reason",
                          value,
                        );
                      }}
                      onBlur={() => setPercent(initialValues)}
                      required={isFieldRequired(
                        "project.terminationRequest.reason",
                      )}
                    />
                  </Col>
                </>
              )}
          </Row>
        </Form>
      </div>
    )
  );
};

export default TerminationRequest;
